import { useState, useEffect, useCallback, useRef } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import CircularProgress from '@mui/material/CircularProgress'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import {
  getFirestore,
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  startAfter,
  where,
  QueryDocumentSnapshot,
  DocumentData
} from 'firebase/firestore'
import Database from '../../../types/Database'

const AUDIT_ACTION_TYPES: Database.AuditActionType[] = [
  'accreditation_approved',
  'accreditation_rejected',
  'payout_processed',
  'payout_credited',
  'refund_credit',
  'refund_monetary',
  'dive_removed',
  'dive_restored',
  'rating_removed',
  'user_suspended',
  'user_banned',
  'user_unsuspended'
]

const PAGE_SIZE = 50

const AuditLogTab = () => {
  const [entries, setEntries] = useState<Database.AuditLogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [actionTypeFilter, setActionTypeFilter] = useState<
    Database.AuditActionType | 'all'
  >('all')
  const [adminUidFilter, setAdminUidFilter] = useState('')

  // Pagination state
  const [lastDoc, setLastDoc] =
    useState<QueryDocumentSnapshot<DocumentData> | null>(null)
  const [hasNextPage, setHasNextPage] = useState(false)
  const pageHistoryRef = useRef<QueryDocumentSnapshot<DocumentData>[]>([])
  const [currentPage, setCurrentPage] = useState(0)

  // Feedback state
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({ open: false, message: '', severity: 'success' })

  const fetchEntries = useCallback(
    async (
      cursor?: QueryDocumentSnapshot<DocumentData> | null,
      direction: 'next' | 'first' = 'first'
    ) => {
      setLoading(true)
      try {
        const db = getFirestore()
        const auditRef = collection(db, 'adminAuditLog')

        const constraints = []

        if (actionTypeFilter !== 'all') {
          constraints.push(where('actionType', '==', actionTypeFilter))
        }

        if (adminUidFilter.trim()) {
          constraints.push(where('adminUid', '==', adminUidFilter.trim()))
        }

        constraints.push(orderBy('timestamp', 'desc'))
        constraints.push(limit(PAGE_SIZE + 1)) // Fetch one extra to check hasNextPage

        if (direction === 'next' && cursor) {
          constraints.push(startAfter(cursor))
        }

        const q = query(auditRef, ...constraints)
        const snapshot = await getDocs(q)

        const docs = snapshot.docs
        const hasMore = docs.length > PAGE_SIZE
        const pageData = hasMore ? docs.slice(0, PAGE_SIZE) : docs

        const entryList = pageData.map(d => ({
          id: d.id,
          ...d.data()
        })) as Database.AuditLogEntry[]

        setEntries(entryList)
        setLastDoc(pageData.length > 0 ? pageData[pageData.length - 1] : null)
        setHasNextPage(hasMore)
      } catch (err) {
        setSnackbar({
          open: true,
          message: `Failed to load audit log: ${
            err instanceof Error ? err.message : 'Unknown error'
          }`,
          severity: 'error'
        })
      } finally {
        setLoading(false)
      }
    },
    [actionTypeFilter, adminUidFilter]
  )

  // Reset pagination when filters change
  useEffect(() => {
    pageHistoryRef.current = []
    setCurrentPage(0)
    fetchEntries(null, 'first')
  }, [fetchEntries])

  const handleNextPage = () => {
    if (!lastDoc) return
    // Save cursor for going back
    pageHistoryRef.current.push(lastDoc)
    setCurrentPage(prev => prev + 1)
    fetchEntries(lastDoc, 'next')
  }

  const handlePreviousPage = () => {
    if (currentPage <= 0) return
    pageHistoryRef.current.pop()
    const prevCursor =
      pageHistoryRef.current.length > 0
        ? pageHistoryRef.current[pageHistoryRef.current.length - 1]
        : null
    setCurrentPage(prev => prev - 1)

    if (prevCursor) {
      fetchEntries(prevCursor, 'next')
    } else {
      fetchEntries(null, 'first')
    }
  }

  const formatDetails = (details: Record<string, unknown>): string => {
    try {
      return JSON.stringify(details, null, 2)
    } catch {
      return String(details)
    }
  }

  if (loading && entries.length === 0) {
    return (
      <Box display='flex' justifyContent='center' py={6}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant='h6' sx={{ mb: 2 }}>
        Audit Log
      </Typography>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size='small' sx={{ minWidth: 200 }}>
          <InputLabel id='audit-action-type-label'>Action Type</InputLabel>
          <Select
            labelId='audit-action-type-label'
            value={actionTypeFilter}
            label='Action Type'
            onChange={e =>
              setActionTypeFilter(
                e.target.value as Database.AuditActionType | 'all'
              )
            }
          >
            <MenuItem value='all'>All Actions</MenuItem>
            {AUDIT_ACTION_TYPES.map(type => (
              <MenuItem key={type} value={type}>
                {type.replace(/_/g, ' ')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label='Admin UID'
          variant='outlined'
          size='small'
          value={adminUidFilter}
          onChange={e => setAdminUidFilter(e.target.value)}
          sx={{ minWidth: 250 }}
          placeholder='Filter by admin UID'
        />
      </Box>

      {/* Audit Log Table */}
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Action Type</TableCell>
              <TableCell>Target Document ID</TableCell>
              <TableCell>Target Collection</TableCell>
              <TableCell>Admin UID</TableCell>
              <TableCell>Timestamp</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align='center'>
                  <Typography color='text.secondary'>
                    No audit log entries found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              entries.map(entry => (
                <TableRow key={entry.id} hover>
                  <TableCell>{entry.actionType.replace(/_/g, ' ')}</TableCell>
                  <TableCell sx={{ maxWidth: 150, wordBreak: 'break-all' }}>
                    {entry.targetDocumentId}
                  </TableCell>
                  <TableCell>{entry.targetCollection}</TableCell>
                  <TableCell sx={{ maxWidth: 150, wordBreak: 'break-all' }}>
                    {entry.adminUid}
                  </TableCell>
                  <TableCell>
                    {entry.timestamp?.toDate?.()
                      ? entry.timestamp.toDate().toLocaleString()
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <Box
                      component='pre'
                      sx={{
                        fontSize: '0.75rem',
                        maxWidth: 300,
                        maxHeight: 100,
                        overflow: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        m: 0
                      }}
                    >
                      {formatDetails(entry.details)}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Button
          variant='outlined'
          size='small'
          disabled={currentPage === 0 || loading}
          onClick={handlePreviousPage}
        >
          Previous Page
        </Button>
        <Typography variant='body2' color='text.secondary'>
          Page {currentPage + 1}
        </Typography>
        <Button
          variant='outlined'
          size='small'
          disabled={!hasNextPage || loading}
          onClick={handleNextPage}
        >
          Next Page
        </Button>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant='filled'
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default AuditLogTab
